import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as permissionApi from '../../api/permission';
import { message } from 'antd';
import { fetchUserPermissions } from './userSlice';
// 获取权限列表
export const fetchPermissionList = createAsyncThunk(
  'permission/fetchPermissionList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await permissionApi.getPermissionList(params);
      if (response && response.code === 200) {
        // 返回数据和查询参数，以便在reducer中保存
        return {
          data: response.data,
          params: params
        };
      }
      return rejectWithValue(response?.message || '获取权限列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取权限列表失败');
    }
  }
);

// 获取权限树
export const fetchPermissionTree = createAsyncThunk(
  'permission/fetchPermissionTree',
  async (_, { rejectWithValue }) => {
    try {
      const response = await permissionApi.getPermissionTree();
      if (response && response.code === 200) {
        // 后端已经返回树形结构，直接使用
        return response.data;
      }
      return rejectWithValue(response?.message || '获取权限树失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取权限树失败');
    }
  }
);

// 创建权限
export const createPermission = createAsyncThunk(
  'permission/createPermission',
  async ({ data, skipRefresh = false }, { rejectWithValue, dispatch }) => {
    try {
      const response = await permissionApi.createPermission(data);
      if (response && response.code === 200) {
        // 创建权限成功后，如果不跳过刷新则刷新用户权限
        if (!skipRefresh) {
          dispatch(fetchUserPermissions(true));
        }
        return response.data;
      } else {
        message.error(response?.message || '创建权限失败');
      }
      return rejectWithValue(response?.message || '创建权限失败');
    } catch (error) {
      return rejectWithValue(error.message || '创建权限失败');
    }
  }
);

// 更新权限
export const updatePermission = createAsyncThunk(
  'permission/updatePermission',
  async ({ id, data, skipRefresh = false }, { rejectWithValue, dispatch }) => {
    try {
      const response = await permissionApi.updatePermission(id, data);
      if (response && response.code === 200) {
        // 更新权限成功后，如果不跳过刷新则刷新用户权限
        if (!skipRefresh) {
          dispatch(fetchUserPermissions(true));
        }
        return response.data;
      }
      return rejectWithValue(response?.message || '更新权限失败');
    } catch (error) {
      return rejectWithValue(error.message || '更新权限失败');
    }
  }
);

// 删除权限
export const deletePermission = createAsyncThunk(
  'permission/deletePermission',
  async ({ id, skipRefresh = false }, { rejectWithValue, dispatch }) => {
    try {
      const response = await permissionApi.deletePermission(id);
      if (response && response.code === 200) {
        // 删除权限成功后，延迟刷新用户权限，避免立即刷新导致页面空白
        // 如果不跳过刷新，则在后台延迟刷新权限，不影响当前页面
        if (!skipRefresh) {
          // 延迟刷新，避免影响当前页面操作
          setTimeout(() => {
            dispatch(fetchUserPermissions(true));
          }, 1000);
        }
        return id;
      }
      return rejectWithValue(response?.message || '删除权限失败');
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error.message || '删除权限失败';
      message.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// 启用权限
export const enablePermission = createAsyncThunk(
  'permission/enablePermission',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await permissionApi.enablePermission(id);
      if (response && response.code === 200) {
        // 启用权限成功后，刷新用户权限
        dispatch(fetchUserPermissions(true));
        return response.data;
      }
      return rejectWithValue(response?.message || '启用权限失败');
    } catch (error) {
      return rejectWithValue(error.message || '启用权限失败');
    }
  }
);

// 禁用权限
export const disablePermission = createAsyncThunk(
  'permission/disablePermission',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await permissionApi.disablePermission(id);
      if (response && response.code === 200) {
        // 禁用权限成功后，刷新用户权限
        dispatch(fetchUserPermissions(true));
        return response.data;
      }
      return rejectWithValue(response?.message || '禁用权限失败');
    } catch (error) {
      return rejectWithValue(error.message || '禁用权限失败');
    }
  }
);  

// 获取角色的权限列表
export const fetchRolePermissions = createAsyncThunk(
  'permission/fetchRolePermissions',
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await permissionApi.getRolePermissions(roleId);
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取角色权限列表失败');
    } catch (error) { 
      return rejectWithValue(error.message || '获取角色权限列表失败');
    }
  }
);

// 获取所有菜单权限树
export const fetchMenuPermissionTree = createAsyncThunk(
  'permission/fetchMenuPermissionTree',
  async (_, { rejectWithValue }) => {
    try { 
      const response = await permissionApi.getMenuPermissionTree();
      if (response && response.code === 200) {
        // 后端已经返回树形结构，直接使用
        return response.data;
      }
      return rejectWithValue(response?.message || '获取菜单权限树失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取菜单权限树失败');
    }
  }
);

// 更新权限菜单树
export const updatePermissionTree = createAsyncThunk(
  'permission/updatePermissionTree',
  async ({ data, skipRefresh = false }, { rejectWithValue, dispatch }) => {
    try {
      const response = await permissionApi.updatePermissionTree(data);
      if (response && response.code === 200) {
        message.success('更新权限菜单树成功');
        // 如果不跳过刷新，则刷新权限树和用户权限
        if (!skipRefresh) {
          dispatch(fetchPermissionTree());
          dispatch(fetchMenuPermissionTree());
          dispatch(fetchUserPermissions(true));
        }
        return response.data;
      }
      message.error(response?.message || '更新权限菜单树失败');
      return rejectWithValue(response?.message || '更新权限菜单树失败');
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error.message || '更新权限菜单树失败';
      message.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// 批量删除权限
export const batchDeletePermissions = createAsyncThunk(
  'permission/batchDeletePermissions',
  async ({ ids, skipRefresh = false }, { rejectWithValue, dispatch }) => {
    try {
      const response = await permissionApi.batchDeletePermissions(ids);
      if (response && response.code === 200) {
        message.success(`成功删除 ${ids.length} 个权限`);
        // 删除权限成功后，延迟刷新用户权限，避免立即刷新导致页面空白
        // 如果不跳过刷新，则在后台延迟刷新权限，不影响当前页面
        if (!skipRefresh) {
          // 延迟刷新，避免影响当前页面操作
          setTimeout(() => {
            dispatch(fetchUserPermissions(true));
          }, 1000);
        }
        return ids;
      }
      message.error(response?.message || '批量删除权限失败');
      return rejectWithValue(response?.message || '批量删除权限失败');
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error.message || '批量删除权限失败';
      message.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// 批量更新权限的父权限
export const batchUpdatePermissionParent = createAsyncThunk(
  'permission/batchUpdatePermissionParent',
  async ({ permissionIds, parentId, skipRefresh = false }, { rejectWithValue, dispatch }) => {
    try {
      const response = await permissionApi.batchUpdatePermissionParent({
        permissionIds,
        parentId
      });
      if (response && response.code === 200) {
        const parentText = parentId === null ? '根节点' : `父权限ID ${parentId}`;
        message.success(`成功将 ${permissionIds.length} 个权限的父权限更新为${parentText}`);
        // 更新权限成功后，延迟刷新用户权限，避免立即刷新导致页面空白
        if (!skipRefresh) {
          setTimeout(() => {
            dispatch(fetchUserPermissions(true));
          }, 1000);
        }
        return { permissionIds, parentId };
      }
      message.error(response?.message || '批量更新权限父级失败');
      return rejectWithValue(response?.message || '批量更新权限父级失败');
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error.message || '批量更新权限父级失败';
      message.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// 批量添加默认CRUD权限
export const createDefaultPermissions = createAsyncThunk(
  'permission/createDefaultPermissions',
  async ({ parentId, autoGrantSuperAdmin = true, skipRefresh = false }, { rejectWithValue, dispatch }) => {
    try {
      const response = await permissionApi.createDefaultPermissions(parentId, autoGrantSuperAdmin);
      if (response && response.code === 200) {
        message.success('批量添加默认CRUD权限成功');
        // 创建权限成功后，如果不跳过刷新则刷新用户权限
        if (!skipRefresh) {
          setTimeout(() => {
            dispatch(fetchUserPermissions(true));
          }, 1000);
        }
        return response.data;
      }
      message.error(response?.message || '批量添加默认CRUD权限失败');
      return rejectWithValue(response?.message || '批量添加默认CRUD权限失败');
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error.message || '批量添加默认CRUD权限失败';
      message.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);



// 初始状态
const initialState = {
  permissionList: {
    data: [],
    total: 0,
    pageNum: 1,
    pageSize: 10,
    loading: false,
    error: null,
    // 添加搜索参数
    searchParams: {
      permissionName: '',
      permissionCode: '',
      permissionType: undefined,
      status: undefined
    }
  },
  permissionTree: {
    data: [],
    loading: false,
    error: null,
  },
  createUpdateStatus: {
    loading: false,
    success: false,
    error: null,
  },
  deleteStatus: {
    loading: false,
    success: false,
    error: null,
  },
  updateTreeStatus: {
    loading: false,
    success: false,
    error: null,
  },
  batchDeleteStatus: {
    loading: false,
    success: false,
    error: null,
  },
  batchUpdateParentStatus: {
    loading: false,
    success: false,
    error: null,
  },
  createDefaultStatus: {
    loading: false,
    success: false,
    error: null,
  },
};

// 创建slice
const permissionSlice = createSlice({
  name: 'permission',
  initialState,
  reducers: {
    clearCreateUpdateStatus: (state) => {
      state.createUpdateStatus = {
        loading: false,
        success: false,
        error: null,
      };
    },
    clearDeleteStatus: (state) => {
      state.deleteStatus = {
        loading: false,
        success: false,
        error: null,
      };
    },
    clearBatchUpdateParentStatus: (state) => {
      state.batchUpdateParentStatus = {
        loading: false,
        success: false,
        error: null,
      };
    },
    clearBatchDeleteStatus: (state) => {
      state.batchDeleteStatus = {
        loading: false,
        success: false,
        error: null,
      };
    },
    clearCreateDefaultStatus: (state) => {
      state.createDefaultStatus = {
        loading: false,
        success: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // 处理获取权限列表
      .addCase(fetchPermissionList.pending, (state) => {
        state.permissionList.loading = true;
        state.permissionList.error = null;
      })
      .addCase(fetchPermissionList.fulfilled, (state, action) => {
        state.permissionList.loading = false;
        state.permissionList.data = action.payload.data.records || [];
        state.permissionList.total = action.payload.data.total || 0;
        // 保存当前分页和搜索参数信息
        if (action.payload.params) {
          state.permissionList.pageNum = action.payload.params.pageNum || 1;
          state.permissionList.pageSize = action.payload.params.pageSize || 10;
          // 保存搜索参数
          state.permissionList.searchParams = {
            permissionName: action.payload.params.permissionName || '',
            permissionCode: action.payload.params.permissionCode || '',
            permissionType: action.payload.params.permissionType,
            status: action.payload.params.status
          };
        }
      })
      .addCase(fetchPermissionList.rejected, (state, action) => {
        state.permissionList.loading = false;
        state.permissionList.error = action.payload;
      })

      // 处理获取权限树
      .addCase(fetchPermissionTree.pending, (state) => {
        state.permissionTree.loading = true;
        state.permissionTree.error = null;
      })
      .addCase(fetchPermissionTree.fulfilled, (state, action) => {
        state.permissionTree.loading = false;
        state.permissionTree.data = action.payload || [];
      })
      .addCase(fetchPermissionTree.rejected, (state, action) => {
        state.permissionTree.loading = false;
        state.permissionTree.error = action.payload;
      })

      // 处理创建权限
      .addCase(createPermission.pending, (state) => {
        state.createUpdateStatus.loading = true;
        state.createUpdateStatus.success = false;
        state.createUpdateStatus.error = null;
      })
      .addCase(createPermission.fulfilled, (state) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.success = true;
      })
      .addCase(createPermission.rejected, (state, action) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.error = action.payload;
      })

      // 处理更新权限
      .addCase(updatePermission.pending, (state) => {
        state.createUpdateStatus.loading = true;
        state.createUpdateStatus.success = false;
        state.createUpdateStatus.error = null;
      })
      .addCase(updatePermission.fulfilled, (state) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.success = true;
      })
      .addCase(updatePermission.rejected, (state, action) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.error = action.payload;
      })

      // 处理删除权限
      .addCase(deletePermission.pending, (state) => {
        state.deleteStatus.loading = true;
        state.deleteStatus.success = false;
        state.deleteStatus.error = null;
      })
      .addCase(deletePermission.fulfilled, (state) => {
        state.deleteStatus.loading = false;
        state.deleteStatus.success = true;
      })
      .addCase(deletePermission.rejected, (state, action) => {
        state.deleteStatus.loading = false;
        state.deleteStatus.error = action.payload;
      })
      
      // 处理启用权限
      .addCase(enablePermission.pending, (state) => {
        state.createUpdateStatus.loading = true;
        state.createUpdateStatus.success = false;
        state.createUpdateStatus.error = null;
      })
      .addCase(enablePermission.fulfilled, (state) => { 
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.success = true;
      })
      .addCase(enablePermission.rejected, (state, action) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.error = action.payload;
      })

      // 处理禁用权限
      .addCase(disablePermission.pending, (state) => {
        state.createUpdateStatus.loading = true;
        state.createUpdateStatus.success = false;
        state.createUpdateStatus.error = null;
      })
      .addCase(disablePermission.fulfilled, (state) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.success = true;
      })
      .addCase(disablePermission.rejected, (state, action) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.error = action.payload;
      })

      // 处理获取角色权限列表
      .addCase(fetchRolePermissions.pending, (state) => {
        state.permissionList.loading = true;
        state.permissionList.error = null;
      })
      .addCase(fetchRolePermissions.fulfilled, (state, action) => {
        state.permissionList.loading = false;
        state.permissionList.data = action.payload || [];
      })
      .addCase(fetchRolePermissions.rejected, (state, action) => {
        state.permissionList.loading = false;
        state.permissionList.error = action.payload;
      })

      // 处理获取所有菜单权限树
      .addCase(fetchMenuPermissionTree.pending, (state) => {
        state.permissionTree.loading = true;
        state.permissionTree.error = null;
      })
      .addCase(fetchMenuPermissionTree.fulfilled, (state, action) => {
        state.permissionTree.loading = false;
        state.permissionTree.data = action.payload || [];
      })
      .addCase(fetchMenuPermissionTree.rejected, (state, action) => {
        state.permissionTree.loading = false;
        state.permissionTree.error = action.payload;
      })

      // 处理更新权限菜单树
      .addCase(updatePermissionTree.pending, (state) => {
        state.updateTreeStatus.loading = true;
        state.updateTreeStatus.success = false;
        state.updateTreeStatus.error = null;
      })
      .addCase(updatePermissionTree.fulfilled, (state) => {
        state.updateTreeStatus.loading = false;
        state.updateTreeStatus.success = true;
      })
      .addCase(updatePermissionTree.rejected, (state, action) => {
        state.updateTreeStatus.loading = false;
        state.updateTreeStatus.error = action.payload;
      })

      // 处理批量删除权限
      .addCase(batchDeletePermissions.pending, (state) => {
        state.batchDeleteStatus.loading = true;
        state.batchDeleteStatus.success = false;
        state.batchDeleteStatus.error = null;
      })
      .addCase(batchDeletePermissions.fulfilled, (state) => {
        state.batchDeleteStatus.loading = false;
        state.batchDeleteStatus.success = true;
      })
      .addCase(batchDeletePermissions.rejected, (state, action) => {
        state.batchDeleteStatus.loading = false;
        state.batchDeleteStatus.error = action.payload;
      })
      // 处理批量更新权限父级
      .addCase(batchUpdatePermissionParent.pending, (state) => {
        state.batchUpdateParentStatus.loading = true;
        state.batchUpdateParentStatus.success = false;
        state.batchUpdateParentStatus.error = null;
      })
      .addCase(batchUpdatePermissionParent.fulfilled, (state) => {
        state.batchUpdateParentStatus.loading = false;
        state.batchUpdateParentStatus.success = true;
      })
      .addCase(batchUpdatePermissionParent.rejected, (state, action) => {
        state.batchUpdateParentStatus.loading = false;
        state.batchUpdateParentStatus.error = action.payload;
      })
      // 处理批量添加默认CRUD权限
      .addCase(createDefaultPermissions.pending, (state) => {
        state.createDefaultStatus.loading = true;
        state.createDefaultStatus.success = false;
        state.createDefaultStatus.error = null;
      })
      .addCase(createDefaultPermissions.fulfilled, (state) => {
        state.createDefaultStatus.loading = false;
        state.createDefaultStatus.success = true;
      })
      .addCase(createDefaultPermissions.rejected, (state, action) => {
        state.createDefaultStatus.loading = false;
        state.createDefaultStatus.error = action.payload;
      });
  },
});

export const { clearCreateUpdateStatus, clearDeleteStatus, clearUpdateTreeStatus, clearBatchDeleteStatus, clearBatchUpdateParentStatus, clearCreateDefaultStatus } = permissionSlice.actions;

export default permissionSlice.reducer; 